import React, { useEffect, useState } from "react";
import { useTranslation } from "@helpers/translate";
import Input from "../shared/Input";
import Checkbox from "../shared/Checkbox";
import GoogleButton from "../../GoogleButton";
import FormButton from "../shared/FormButton";
import LoginVerification from "./LoginVerification";
import BinifyLogo from "@components/library/Icons/BinifyLogo";
import {
  getEmailStatus,
  getEmailMessage,
  isEmailValid,
} from "@utils/validation";
import "../AuthForm.sass";
import { useDispatch } from "react-redux";
import { applyAuthResponse, loginVerify, sendLogin } from "@actions";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";

const GOOGLE_AUTH_URL = "https://api.binify.app/api/auth/google";

const REMEMBER_ME_KEY = "remember_me";
const REMEMBERED_EMAIL_KEY = "remembered_email";

const EMAIL_INPUT_ID = "login-email";
const PASSWORD_INPUT_ID = "login-password";
const VERIFICATION_INPUT_ID = "login-verification";

const LoginForm = ({ setView, isMobile = false }) => {
  const { __ } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [myData, setMyData] = useState({});
  const [showVerification, setShowVerification] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");

  const { handleSubmit } = useForm({ mode: "onChange" });

  useEffect(() => {
    const savedRememberMe = localStorage.getItem(REMEMBER_ME_KEY) === "true";
    const savedEmail = localStorage.getItem(REMEMBERED_EMAIL_KEY) || "";

    if (savedRememberMe && savedEmail) {
      setRememberMe(true);
      setEmail(savedEmail);
    }
  }, []);

  const passwordMinLengthMessage = __("auth.passwordMinLength");
  const isPasswordTooShort = password.length > 0 && password.length < 8;

  const passwordErrorMessage = isPasswordTooShort
    ? passwordMinLengthMessage
    : error;

  const clearError = () => {
    if (error) {
      setError("");
    }

    if (resendSuccess) {
      setResendSuccess(false);
    }
  };

  const focusInput = (inputId) => {
    const inputElement = document.getElementById(inputId);

    if (inputElement) {
      inputElement.focus();
    }
  };

  const saveRememberedLogin = () => {
    if (rememberMe) {
      localStorage.setItem(REMEMBER_ME_KEY, "true");
      localStorage.setItem(REMEMBERED_EMAIL_KEY, email);
      return;
    }

    localStorage.removeItem(REMEMBER_ME_KEY);
    localStorage.removeItem(REMEMBERED_EMAIL_KEY);
  };

  const getLoginErrorMessage = (err) => {
    let backendMessage = "";

    if (
      err &&
      err.response &&
      err.response.data &&
      err.response.data.message
    ) {
      backendMessage = err.response.data.message;
    } else if (err && err.message) {
      backendMessage = err.message;
    }

    if (
      backendMessage ===
      "E-mail combination is wrong or user doesn't exist"
    ) {
      return __("auth.user_not_found");
    }

    if (
      backendMessage ===
      "E-mail and/or password combination is wrong or user doesn't exist"
    ) {
      return __("auth.wrong_password_or_email");
    }

    if (err && err.response && err.response.status === 422) {
      return __("auth.wrong_password_or_email");
    }

    return backendMessage || __("auth.wrong_credentials");
  };

  const handleGoogleAuth = () => {
    setLoading(true);

    const p_hash = localStorage.getItem("p_hash");
    const query = p_hash ? `?p_hash=${encodeURIComponent(p_hash)}` : "";

    window.location.href = `${GOOGLE_AUTH_URL}${query}`;
  };

  const handleLogin = () => {
    setLoading(true);
    setError("");
    setResendSuccess(false);

    sendLogin(email, password)
      .then((res) => {
        // Backend with 2FA disabled returns the session right away.
        if (res && res.token) {
          return dispatch(applyAuthResponse(res)).then(() => {
            saveRememberedLogin();
            navigate("/");
          });
        }

        setMyData(res);
        setShowVerification(true);
        setVerificationCode("");
      })
      .catch((err) => {
        setError(getLoginErrorMessage(err));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleResendCode = () => {
    if (loading || resendLoading) {
      return;
    }

    setResendLoading(true);
    setResendSuccess(false);
    setError("");

    sendLogin(email, password)
      .then((res) => {
        setMyData((currentData) => ({
          ...currentData,
          ...res,
        }));

        setVerificationCode("");
        setResendSuccess(true);

        window.requestAnimationFrame(() => {
          focusInput(VERIFICATION_INPUT_ID);
        });
      })
      .catch((err) => {
        setError(getLoginErrorMessage(err));
      })
      .finally(() => {
        setResendLoading(false);
      });
  };

  const handleVerificationCodeChange = (event) => {
    clearError();

    const value = event.target.value;

    if (/^\d*$/.test(value) && value.length <= 6) {
      setVerificationCode(value);
    }
  };

  const handleVerification = () => {
    if (loading || resendLoading) {
      return;
    }

    if (verificationCode.length !== 6) {
      focusInput(VERIFICATION_INPUT_ID);
      return;
    }

    setLoading(true);
    setError("");
    setResendSuccess(false);

    dispatch(
      loginVerify({
        email,
        password,
        verification: verificationCode,
        hash: myData.hash,
      }),
    )
      .then(() => {
        saveRememberedLogin();
        navigate("/");
      })
      .catch((err) => {
        setVerificationCode("");

        if (err && err.response && err.response.status === 400) {
          setError(__("auth.issue_verifying_code"));
          return;
        }

        let message = "";

        if (
          err &&
          err.response &&
          err.response.data &&
          err.response.data.message
        ) {
          message = err.response.data.message;
        } else if (err && err.message) {
          message = err.message;
        }

        setError(message || __("auth.issue_verifying_code"));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onSubmit = () => {
    if (loading || resendLoading) {
      return;
    }

    if (showVerification) {
      handleVerification();
      return;
    }

    if (!email.trim() || !isEmailValid(email)) {
      focusInput(EMAIL_INPUT_ID);
      return;
    }

    if (!password) {
      focusInput(PASSWORD_INPUT_ID);
      return;
    }

    if (password.length < 8) {
      setError(passwordMinLengthMessage);
      focusInput(PASSWORD_INPUT_ID);
      return;
    }

    handleLogin();
  };

  return (
    <div className={`auth-form${isMobile ? " auth-form--mobile" : ""}`}>
      <form
        className={`auth-form__form${
          isMobile ? " auth-form__form--mobile" : ""
        }`}
        onSubmit={handleSubmit(onSubmit)}
      >
        {!isMobile && <BinifyLogo width="98" height="18" />}

        {!showVerification && (
          <h1
            className={`auth-form__title${
              isMobile ? " auth-form__title--mobile" : ""
            }`}
          >
            {__("auth.sign_in")}
          </h1>
        )}

        {!showVerification && !isMobile && (
          <GoogleButton
            variant="light"
            type="button"
            className="auth-form__google-button"
            isSignUp={false}
            onClick={handleGoogleAuth}
            disabled={loading}
          />
        )}

        {!showVerification && !isMobile && (
          <div className="auth-form__divider-wrapper">
            <div className="auth-form__divider-line" />

            <span className="auth-form__divider-text">
              {__("auth.signInWithEmail")}
            </span>
          </div>
        )}

        {!showVerification && (
          <>
            <Input
              id={EMAIL_INPUT_ID}
              label={__("auth.emailAddress")}
              showRequiredMark={true}
              type="email"
              message={
                getEmailMessage(email) && email.length > 0
                  ? __("auth.pleaseEnterValidEmail")
                  : ""
              }
              messageStatus="error"
              status={getEmailStatus(email)}
              value={email}
              onChange={(event) => {
                clearError();
                setEmail(event.target.value);
              }}
              className={`auth-form__input-wrapper${
                isMobile ? " auth-form__input-wrapper--mobile" : ""
              }`}
            />

            <Input
              id={PASSWORD_INPUT_ID}
              label={__("auth.password")}
              showRequiredMark={true}
              type="password"
              value={password}
              onChange={(event) => {
                clearError();
                setPassword(event.target.value);
              }}
              className={`auth-form__input-wrapper${
                isMobile
                  ? " auth-form__input-wrapper--mobile auth-form__input-wrapper--password-mobile"
                  : " auth-form__input-wrapper--password-login"
              }`}
              message={passwordErrorMessage}
              messageStatus="error"
            />

            {!passwordErrorMessage && (
              <div className="auth-form__error-placeholder auth-form__error-placeholder--login" />
            )}

            <div className="auth-form__login-options">
              <Checkbox
                className="auth-form__remember-me"
                checked={rememberMe}
                onChange={(event) => {
                  setRememberMe(event.target.checked);
                }}
              >
                {__("auth.rememberMe")}
              </Checkbox>

              <a
                href="#"
                className={`auth-form__forgot-password${
                  isMobile ? " auth-form__forgot-password--mobile" : ""
                }`}
                onClick={(event) => {
                  event.preventDefault();
                  setView("forgot-password");
                }}
              >
                {__("auth.forgotPassword")}
              </a>
            </div>

            <FormButton
              variant="secondary"
              type="submit"
              className={`auth-form__button-wrapper auth-form__button-wrapper--uppercase${
                isMobile ? " auth-form__button-wrapper--mobile" : ""
              }`}
              isLoading={loading}
            >
              {__("auth.signIn")}
            </FormButton>
          </>
        )}

        {showVerification && (
          <LoginVerification
            email={email}
            verificationType={myData["2fa_type"]}
            verificationCode={verificationCode}
            error={error}
            loading={loading}
            resendLoading={resendLoading}
            resendSuccess={resendSuccess}
            onCodeChange={handleVerificationCodeChange}
            onVerify={handleVerification}
            onResend={handleResendCode}
          />
        )}

        {!showVerification && isMobile && (
          <>
            <div className="auth-form__divider-wrapper auth-form__divider-wrapper--mobile">
              <div className="auth-form__divider-line" />

              <span className="auth-form__divider-text">
                {__("auth.signInWith")}
              </span>
            </div>

            <GoogleButton
              variant="light"
              type="button"
              className="auth-form__google-button auth-form__google-button--mobile"
              isSignUp={false}
              onClick={handleGoogleAuth}
              disabled={loading}
            />
          </>
        )}

        {!showVerification && (
          <p
            className={`auth-form__text${
              isMobile ? " auth-form__text--mobile" : ""
            }`}
          >
            {__("auth.firstTime")}{" "}
            <a
              href="#"
              className="auth-form__link"
              onClick={(event) => {
                event.preventDefault();
                setView("signup");
              }}
            >
              {__("auth.signUp")}
            </a>
          </p>
        )}
      </form>
    </div>
  );
};

export default LoginForm;
